import React, { useState } from "react";
import { motion } from "framer-motion";
import { Info, MapPin, Loader2, ExternalLink } from "lucide-react";
import { fmtUSD, fmtNum, parseNum } from "../../lib/format";
import { StreetViewImage } from "./StreetViewImage";

// Official Zillow logo (owner-supplied approved asset at /public/zillow-logo.webp).
// Falls back to the Zillow wordmark text until the official asset is present.
const ZillowLogo = () => {
  const [broke, setBroke] = useState(false);
  if (broke) return <span data-testid="zillow-logo" className="font-bold text-[15px] leading-none text-[#006AFF]">Zillow</span>;
  return <img src="/zillow-logo.webp" alt="Zillow" loading="lazy" onError={() => setBroke(true)} className="h-5 w-auto shrink-0" data-testid="zillow-logo" />;
};

const Method = ({ label, value, sub, testid, accent, loading }) => (
  <div className={`rounded-xl border p-4 ${accent ? "border-brand-emerald bg-brand-emeraldLight/60" : "border-brand-hairline bg-brand-sand/50"}`} data-testid={testid}>
    <p className="text-[0.65rem] uppercase tracking-[0.18em] text-brand-stone">{label}</p>
    {loading ? (
      <p className="mt-2 flex items-center gap-2 text-sm text-brand-stone"><Loader2 className="h-4 w-4 animate-spin text-brand-emerald" /> Appraising…</p>
    ) : (
      <p className={`mt-1.5 font-mono font-bold text-2xl sm:text-3xl ${accent ? "text-brand-emerald" : "text-brand-ink"}`}>{fmtUSD(value)}</p>
    )}
    {sub && !loading && <p className="mt-1 text-xs text-brand-stone leading-snug">{sub}</p>}
  </div>
);

export default function ValuationResult({ property, panel, panelLoading, owner, liveZestimate, onBrokerReview }) {
  const v = property.valuation;
  const a = property.address || {};
  const specs = [
    property.beds != null && `${property.beds} bd`,
    property.baths != null && `${property.baths} ba`,
    property.living_area_sqft && `${fmtNum(property.living_area_sqft)} sq ft`,
    property.year_built && `Built ${property.year_built}`,
  ].filter(Boolean).join(" · ");

  const AddressBlock = () => (
    <div data-testid="valuation-address-block">
      <div className="flex items-start gap-2">
        <MapPin className="h-5 w-5 text-brand-gold shrink-0 mt-1" />
        <div>
          <h1 className="font-serif text-2xl sm:text-3xl text-brand-ink leading-tight" data-testid="valuation-address-line1">
            {a.line1}{a.unit ? `, Unit ${a.unit}` : ""}
          </h1>
          <p className="text-sm text-brand-stone" data-testid="valuation-address-line2">{a.city}, {a.state} {a.zip}</p>
        </div>
      </div>
      {specs && <p className="mt-2 text-xs uppercase tracking-wider text-brand-stone/80 font-mono" data-testid="valuation-specs-line">{specs}</p>}
    </div>
  );

  if (!v?.available) {
    return (
      <div className="rounded-2xl bg-white border border-brand-hairline p-8" data-testid="valuation-unavailable">
        <AddressBlock />
        <h2 className="mt-6 font-serif text-2xl text-brand-ink">A reliable estimate isn't available for this property.</h2>
        <p className="mt-3 text-sm text-brand-stone">We won't show a value we can't stand behind. Request a broker-reviewed valuation instead.</p>
        <button onClick={onBrokerReview} data-testid="broker-review-unavailable-btn"
          className="mt-5 rounded-xl bg-brand-emerald px-6 py-3 text-brand-cream hover:bg-brand-emeraldHover">Request broker-reviewed valuation</button>
      </div>
    );
  }

  const panelOk = panel?.ok && panel.consensus_value;
  const zil = parseNum(owner?.zestimate);
  const red = parseNum(owner?.redfin);
  const fullAddress = `${a.line1}${a.unit ? `, Unit ${a.unit}` : ""}, ${a.city}, ${a.state} ${a.zip}`.replace(/\s+/g, " ").trim();
  const zLive = !!(liveZestimate && liveZestimate.value);
  const zillowUrl = (zLive && liveZestimate.zillow_url) ? liveZestimate.zillow_url : `https://www.zillow.com/homes/${encodeURIComponent(fullAddress)}_rb/`;
  const values = [v.estimate, panelOk && panel.consensus_value, zil > 0 && zil, red > 0 && red].filter(Boolean);
  const lo = Math.min(...values), hi = Math.max(...values);
  const rangePct = v.estimate ? Math.round(((v.high - v.low) / 2 / v.estimate) * 100) : 0;
  const lowConfidence = rangePct > 15;

  return (
    <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
      className="rounded-2xl bg-white border border-brand-hairline p-6 sm:p-8 shadow-[0_4px_20px_rgba(27,59,43,0.04)]" data-testid="valuation-result-card">
      <StreetViewImage lat={a.lat} lng={a.lng} w={1024} h={420}
        alt={`Street view of ${a.line1}`}
        className="mb-6 w-full h-44 sm:h-56 object-cover rounded-xl border border-brand-hairline" />
      <AddressBlock />

      <div className="mt-6">
        <span className="eyebrow">{v.owner_adjusted ? "Adjusted Estimate (Owner Updates & RentCast AVM)" : "Estimated Market Value"}</span>
        <p className="mt-2 font-mono font-bold tracking-tight text-3xl sm:text-4xl lg:text-5xl text-brand-emerald" data-testid="valuation-main-estimate">
          {values.length > 1 && lo !== hi ? `${fmtUSD(lo)} – ${fmtUSD(hi)}` : fmtUSD(v.estimate)}
        </p>
        <p className="mt-1 text-xs text-brand-stone" data-testid="valuation-headline-note">
          {values.length > 1 ? `Across ${values.length} valuation methods — the honest answer depends on the method.` : "RentCast automated valuation."}
        </p>
      </div>

      <div className="mt-5 grid sm:grid-cols-2 gap-3">
        <Method label="RentCast AVM" value={v.estimate} testid="method-rentcast"
          sub={`Range ${fmtUSD(v.low)} – ${fmtUSD(v.high)} · Confidence ${v.confidence}%`} />
        <Method label="AI Appraisal Panel" value={panelOk ? panel.consensus_value : null} testid="method-panel" accent
          loading={panelLoading || (!panel && !panelLoading)}
          sub={panelOk ? `Median of ${panel.valid_count} models · ${panel.agreement_label} (${panel.agreement_score}%)` : panel && !panel.ok ? "Panel unavailable" : null} />
        {zil > 0 && (
          <div className="rounded-xl border border-brand-hairline bg-brand-sand/50 p-4" data-testid="method-zillow">
            <div className="flex items-center justify-between gap-3">
              <p className="text-[0.65rem] uppercase tracking-[0.18em] text-brand-stone">{zLive ? "Zillow Zestimate® (live)" : "Zillow Zestimate® (owner-entered)"}</p>
              <ZillowLogo />
            </div>
            <p className="mt-1.5 font-mono font-bold text-2xl sm:text-3xl text-brand-ink">{fmtUSD(zil)}</p>
            {zLive ? (
              <p className="mt-1 text-xs text-brand-emerald font-semibold" data-testid="zillow-result-live-badge">Live · Auto-detected via Bridge Interactive</p>
            ) : (
              <p className="mt-1 text-xs text-brand-stone leading-snug">Typed in by you · not verified</p>
            )}
            <p className="mt-1.5 text-[0.7rem] italic text-brand-stone/80" data-testid="zillow-as-is-disclaimer">Data is provided "as is" via the Zestimate® API.</p>
            <a href={zillowUrl} target="_blank" rel="noopener noreferrer" data-testid="zillow-property-link"
              className="mt-2 inline-flex items-center gap-1 text-[0.72rem] font-semibold text-[#006AFF] hover:underline">
              See more details for {fullAddress} on Zillow <ExternalLink className="h-3 w-3" />
            </a>
          </div>
        )}
        {red > 0 && <Method label="Redfin (owner-entered)" value={red} testid="method-redfin" sub="Typed in by you · not verified" />}
      </div>

      {lowConfidence && (
        <div className="mt-4 flex items-start gap-2 rounded-xl border border-brand-gold/40 bg-brand-goldMuted p-3" data-testid="low-confidence-warning">
          <Info className="h-4 w-4 text-brand-gold shrink-0 mt-0.5" />
          <p className="text-xs text-brand-ink leading-relaxed">
            <strong>Wide value range (±{rangePct}%).</strong> Nearby sales vary a lot in size and age, so the automated model is less certain here. Lean on the AI Panel and consider a broker-reviewed valuation.
          </p>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        {v.is_demo && <span className="rounded-full bg-brand-gold/15 text-brand-gold px-3 py-1 text-xs font-semibold" data-testid="demo-badge">Demonstration data</span>}
        {v.owner_adjusted && <span className="rounded-full bg-brand-gold/15 text-brand-gold px-3 py-1 text-xs font-semibold" data-testid="owner-adjusted-badge">Owner-updated specs</span>}
        <span className="rounded-full bg-brand-sand text-brand-stone px-3 py-1 text-xs">Source: {v.source}</span>
        <span className="rounded-full bg-brand-sand text-brand-stone px-3 py-1 text-xs">As of {v.valuation_date}</span>
      </div>

      <div className="mt-5 flex items-start gap-2 rounded-xl bg-brand-sand p-4">
        <Info className="h-4 w-4 text-brand-stone shrink-0 mt-0.5" />
        <p className="text-xs text-brand-stone leading-relaxed">{v.disclaimer}</p>
      </div>
    </motion.div>
  );
}
